import { Fragment, useId, useState, type ReactNode } from "react";
import pantherMarkUrl from "../assets/panther-mark.webp";
import { SOURCES } from "../methodology";

export const destinations = [
  { path: "/tokenization", label: "Tokenization", icon: "◈", description: "Real assets. On-chain intelligence.", code: "01" },
  { path: "/investment-analysis", label: "Investment Analysis", icon: "⌁", description: "Follow the flows. Find your edge.", code: "02" },
  { path: "/research", label: "Research", icon: "▤", description: "Go deeper than the headline.", code: "03" },
  { path: "/daily-digest", label: "Daily Digest", icon: "◷", description: "Your perspective on the market.", code: "04" },
];

/** The asset classes and the league tables, nested under Tokenization in the sidebar (U-17). */
export const tokenizationSections = [
  { path: "/tokenization", label: "Tokenized stocks" },
  { path: "/tokenization/leagues", label: "League tables" },
  { path: "/tokenization/stablecoins", label: "Stablecoins" },
  { path: "/tokenization/treasuries", label: "U.S. Treasuries" },
  { path: "/tokenization/govt-debt", label: "Government debt" },
  { path: "/tokenization/credit", label: "Private credit" },
  { path: "/tokenization/money-market", label: "Money market" },
  { path: "/tokenization/commodities", label: "Commodities" },
  { path: "/tokenization/private-equity", label: "Private equity" },
  { path: "/tokenization/real-estate", label: "Real estate" },
];

/** Research tools, nested under Research in the sidebar. */
export const researchSections = [
  { path: "/research/insider-trades", label: "Insider trades" },
];

/** Tools built on the data: first and highlighted under Tokenization in the sidebar, and in its header. */
export const tools = [
  { path: "/tokenization/onboarding", label: "Portfolio builder", icon: "◆", badge: "NEW" },
];

/** The current path without a trailing slash ("" for the root). */
const currentPath = () => (typeof location === "undefined" ? "" : location.pathname.replace(/\/+$/, ""));

export function Brand() {
  return <a className="brand" href="/tokenization" aria-label="Panther Terminal home"><img src={pantherMarkUrl} alt="" /><span>PANTHER<span className="brand-sub">TERMINAL</span></span></a>;
}

/** A sidebar section with its pages listed under it: expanded while inside it, collapsed elsewhere until opened. */
function NavGroup({ destination: d, active, children }: { destination: (typeof destinations)[number]; active: boolean; children: ReactNode }) {
  const [expanded, setExpanded] = useState(active);
  const subId = useId();
  return <div className="nav-group">
    <div className="nav-parent">
      {/* "true", not "page": the sub-item below is the page; the parent is the current section. */}
      <a href={d.path} aria-current={active ? "true" : undefined}><span className="nav-icon">{d.icon}</span>{d.label}</a>
      <button className="nav-chevron" type="button" aria-expanded={expanded} aria-controls={subId} aria-label={`${expanded ? "Collapse" : "Expand"} ${d.label}`} onClick={() => setExpanded(!expanded)}><span aria-hidden>▾</span></button>
    </div>
    <ul className="nav-sub" id={subId} hidden={!expanded}>{children}</ul>
  </div>;
}

const credited = SOURCES.flatMap((s) => (s.url ? [{ name: s.name, url: s.url }] : []));

/** One quiet line under every Tokenization page crediting the outside data sources. */
function Credits() {
  return <p className="credits">Data: on-chain reads, issuers, {credited.map((s, i) => <Fragment key={s.name}>{i === 0 ? "" : i === credited.length - 1 ? " and " : ", "}<a href={s.url} rel="noreferrer" target="_blank">{s.name}</a></Fragment>)} · <a href="/tokenization/methodology#sources">Sources</a></p>;
}

export function SiteShell({ children, section = "Tokenization" }: { children: ReactNode; section?: string }) {
  const [open, setOpen] = useState(false);
  const tokenization = section === "Tokenization";
  const research = section === "Research";
  const path = currentPath();
  return <div className="workspace">
    <aside className="sidebar" data-open={open}>
      <Brand />
      <button className="mobile-toggle" onClick={() => setOpen(!open)} aria-expanded={open}>Menu {open ? "−" : "+"}</button>
      <div className="sidebar-body">
        <p className="nav-caption">WORKSPACE</p>
        <nav aria-label="Main navigation">{destinations.map(d => d.path === "/tokenization" ? (
          <NavGroup key={d.path} destination={d} active={tokenization}>
            {tools.map(tool => <li key={tool.path} className="nav-tool"><a href={tool.path} aria-current={path === tool.path ? "page" : undefined}><span className="nav-tool-icon">{tool.icon}</span>{tool.label}<span className="nav-badge">{tool.badge}</span></a></li>)}
            {tokenizationSections.map(item => <li key={item.path}><a href={item.path} aria-current={path === item.path ? "page" : undefined}><span className="nav-dot" />{item.label}</a></li>)}
          </NavGroup>
        ) : d.path === "/research" ? (
          <NavGroup key={d.path} destination={d} active={research}>
            {researchSections.map(item => <li key={item.path}><a href={item.path} aria-current={path === item.path ? "page" : undefined}><span className="nav-dot" />{item.label}</a></li>)}
          </NavGroup>
        ) : <a key={d.path} href={d.path} aria-current={section === d.label ? "page" : undefined}><span className="nav-icon">{d.icon}</span>{d.label}<span className="nav-arrow">↗</span></a>)}</nav>
        {tokenization && <>
        <div className="sidebar-bottom"><span className="nav-caption">BUILT FOR THE CURIOUS</span><p>The next market is already here.</p><a href="/tokenization/methodology">Data & methodology ↗</a><span className="version">PANTHER / V.02</span></div></>}
      </div>
    </aside>
    <div className="workspace-main"><header className="workspace-top"><span>Workspace <span className="breadcrumb">/</span> <strong>{section}</strong></span>{tokenization && <span className="system-label"><i /> PUBLIC MARKET DATA</span>}</header>{tokenization && <div className="workspace-heading"><div><span className="eyebrow">PANTHER INTELLIGENCE / TOKENIZATION{path === "/tokenization/onboarding" ? " / TOOLS" : ""}</span>{path === "/tokenization/onboarding" ? <><h1>Portfolio builder</h1><p>Turn capital into a tokenized portfolio: a stock and bond mix, a glide path, and where each order fills on chain.</p></> : <><h1>Tokenization</h1><p>Real assets. On-chain intelligence.</p></>}</div><div className="heading-actions">{path !== "/tokenization/onboarding" && <a className="signal-link" href="/tokenization/onboarding">◆ Build a portfolio →</a>}<a className="outline-link" href="/tokenization/methodology">How we count ↗</a></div></div>}{research && <div className="workspace-heading"><div><span className="eyebrow">PANTHER INTELLIGENCE / RESEARCH</span><h1>Insider trades</h1><p>Buys and sells by a company's officers, directors and large holders, straight from SEC filings, as far back as you choose.</p></div></div>}{children}{tokenization && <Credits />}</div>
  </div>;
}
